// Pure ΔT (delta-time) scale: a piecewise-uniform sequence of hour-stops around
// a fixed t0. Config is a list of {fromHour, step} pairs, sorted ascending by
// fromHour, each pair's step applies from its fromHour up to the next pair's
// fromHour (or maxHours for the last pair). Breakpoints must land exactly on a
// stop of both adjacent segments, or gaps/duplicates can appear.
export const DEFAULT_SEGMENTS = [
  { fromHour: 0, step: 1 },
  { fromHour: 24, step: 2 },
  { fromHour: 72, step: 4 }
]

const roundStop = (hours) => Math.round(hours * 1e6) / 1e6

const buildStops = (segments, maxHours) => {
  const sorted = [...segments].sort((a, b) => a.fromHour - b.fromHour)
  const stops = [sorted[0].fromHour]

  sorted.forEach((seg, i) => {
    const nextBoundary = i + 1 < sorted.length ? sorted[i + 1].fromHour : maxHours
    let current = seg.fromHour
    while (current + seg.step <= nextBoundary + 1e-9) {
      current += seg.step
      stops.push(roundStop(current))
    }
  })

  return stops.filter((h) => h <= maxHours + 1e-9)
}

const segmentFor = (segments, hours) => {
  const sorted = [...segments].sort((a, b) => a.fromHour - b.fromHour)
  let segment = sorted[0]
  for (const seg of sorted) {
    if (seg.fromHour <= hours) segment = seg
    else break
  }
  return segment
}

// Builds an immutable ΔT scale: the sorted list of valid hour-stops plus
// pure lookup helpers between an integer step-index and its hour value.
export const createDeltaTScale = ({ segments = DEFAULT_SEGMENTS, maxHours }) => {
  if (!Number.isFinite(maxHours) || maxHours < 0) {
    throw new Error("createDeltaTScale: maxHours must be a non-negative finite number")
  }

  const stops = buildStops(segments, maxHours)

  const indexToHours = (n) => {
    const clamped = Math.min(Math.max(0, Math.round(n)), stops.length - 1)
    return stops[clamped]
  }

  // Nearest stop for an arbitrary hour value; ties round up. Returns its index.
  const hoursToIndex = (hours) => {
    const clampedHours = Math.min(Math.max(0, hours), maxHours)
    const segment = segmentFor(segments, clampedHours)
    // Round-half-up within the segment the raw value falls into.
    const k = Math.floor((clampedHours - segment.fromHour) / segment.step + 0.5)
    const candidate = roundStop(segment.fromHour + k * segment.step)

    // Binary search confirms the true index and acts as a safety net against
    // floating point drift, `candidate` should already equal a real stop.
    let lo = 0
    let hi = stops.length - 1
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2)
      if (stops[mid] < candidate) lo = mid + 1
      else hi = mid
    }
    if (lo === 0) return 0
    const distUpper = Math.abs(stops[lo] - candidate)
    const distLower = Math.abs(stops[lo - 1] - candidate)
    return distLower < distUpper ? lo - 1 : lo
  }

  const snapHours = (hours) => stops[hoursToIndex(hours)]

  return { stops, indexToHours, hoursToIndex, snapHours }
}

// The same scale as seen by a slider: index <-> value instead of index <-> hours, with an optional
// extra stop at index 0 that means "not known" and reads back as `null`.
//
// It's a separate layer rather than an option on createDeltaTScale because the hour scale itself is
// unchanged by it, the stops, and everything that depends on them, stay exactly where they were.
// Only the numbering shifts, and only when `allowUnknown` is on.
export const createIndexedDeltaTScale = ({ segments = DEFAULT_SEGMENTS, maxHours, allowUnknown = false }) => {
  const scale = createDeltaTScale({ segments, maxHours })
  const offset = allowUnknown ? 1 : 0

  return {
    stops: scale.stops,
    lastIndex: scale.stops.length - 1 + offset,
    isUnknownIndex: (index) => allowUnknown && index === 0,
    // Anything that isn't a number counts as "not known", that's how an unset field arrives.
    valueToIndex: (value) => (allowUnknown && typeof value !== "number")
      ? 0
      : scale.hoursToIndex(value) + offset,
    indexToValue: (index) => (allowUnknown && index === 0)
      ? null
      : scale.indexToHours(index - offset)
  }
}

// Plain duration text, no direction sign, this is what the API string
// (`egg_larvae_age`) should carry, since it describes an age, not a delta.
export const formatHoursDuration = (hours) => {
  if (hours < 24) return `${hours}u`
  const days = Math.floor(hours / 24)
  const rest = hours % 24
  return rest === 0 ? `${days}d` : `${days}d ${rest}u`
}

// "1d", "6u", "2d 3u" → uren. Het omgekeerde van `formatHoursDuration`, nodig om een opgeslagen
// leeftijd weer op de schuif te kunnen zetten.
//
// Het veld is bij de API vrije tekst, dus er kan ook iets in staan wat hier niet vandaan komt
// ("2 dagen", of een waarde uit een andere client). Dat levert `null`: dan liever geen stand dan
// een verkeerde. De aanroeper houdt de oorspronkelijke tekst gewoon in de formulierwaarden.
export const parseHoursDuration = (text) => {
  if (typeof text === "number") return Number.isFinite(text) ? text : null
  if (typeof text !== "string") return null

  const match = text.trim().match(/^(?:(\d+)\s*d)?\s*(?:(\d+)\s*u)?$/i)
  if (!match || (match[1] === undefined && match[2] === undefined)) return null

  return Number(match[1] || 0) * 24 + Number(match[2] || 0)
}
